/**
 * activityTracker — globaal overzicht van langlopende acties (offerte-concept
 * genereren, AI-check, Frits check, Gripp-push, …) + "seintje" als iets klaar is.
 *
 * Gebruik:
 *   const act = startActivity('Concept genereren', { path: '/offertes' });
 *   try { ...; act.done('Concept staat klaar'); } catch (e) { act.fail(e.message); }
 *
 * De NavBar toont een indicator (ActivityIndicator) zolang er iets loopt of
 * recent is afgerond. Is de gebruiker intussen ergens anders (ander tabblad of
 * andere pagina), dan volgt een browser-notificatie.
 */

let items = [];
const listeners = new Set();
let seq = 0;

const emit = () => listeners.forEach((fn) => { try { fn(items); } catch { /* listener weg */ } });

export function subscribeActivities(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function getActivities() { return items; }

export function dismissActivity(id) {
  items = items.filter((a) => a.id !== id);
  emit();
}
export function clearFinishedActivities() {
  items = items.filter((a) => a.status === 'running');
  emit();
}

function maybeNotify(activity) {
  // Alleen een seintje als je er niet (meer) naar zit te kijken:
  // ander tabblad/app, óf binnen de app naar een andere pagina genavigeerd.
  const onOtherPage = activity.path
    && !window.location.pathname.toLowerCase().startsWith(String(activity.path).toLowerCase());
  if (!document.hidden && !onOtherPage) return;
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const failed = activity.status === 'error';
    const n = new Notification(
      failed ? `⚠️ ${activity.label} — mislukt` : `✅ ${activity.label} — klaar`,
      { body: activity.message || '', tag: `ff-activity-${activity.id}` }
    );
    n.onclick = () => { window.focus(); n.close(); };
  } catch { /* notificaties zijn nice-to-have */ }
}

/**
 * Start een langlopende actie. Geeft { done(message?), fail(message?) } terug.
 * `path` = de pagina waar de actie bij hoort (voor "ga ernaartoe" + seintje-logica).
 */
export function startActivity(label, { path } = {}) {
  const id = ++seq;
  const activity = {
    id, label,
    path: path || window.location.pathname,
    startedAt: Date.now(),
    status: 'running',
    message: null,
    endedAt: null,
    seen: false,
  };
  items = [activity, ...items].slice(0, 25);
  emit();
  // Permissie lazy aanvragen bij de eerste langlopende actie
  try {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  } catch { /* geen support */ }

  const finish = (status, message) => {
    let finished = null;
    items = items.map((a) => {
      if (a.id !== id) return a;
      finished = { ...a, status, message: message || null, endedAt: Date.now() };
      return finished;
    });
    emit();
    if (finished) maybeNotify(finished);
  };
  return {
    done: (message) => finish('done', message),
    fail: (message) => finish('error', message),
  };
}

/** Afgeronde items als 'gezien' markeren (badge dooft) — bij openen van het overzicht. */
export function markActivitiesSeen() {
  let changed = false;
  items = items.map((a) => {
    if (a.status !== 'running' && !a.seen) { changed = true; return { ...a, seen: true }; }
    return a;
  });
  if (changed) emit();
}
